import { createBrowserRouter, Link, Navigate, RouterProvider, type RouteObject } from "react-router";
import { AppShell, PublicShell, RequireAuth } from "./core/layout";
import { modules } from "./core/modules";
import { AccountPage } from "./core/pages/account";
import { ForgotPage, InvitationPage, LoginPage, ResetPage, SignupPage } from "./core/pages/auth";
import { OrgHomePage, OrgPickerPage } from "./core/pages/orgs";
import { AuditPage, MembersPage, OutboxPage, ServicesPage } from "./core/pages/settings";
import { useSession } from "./core/session";
import { Loading } from "./core/ui";

function Home() {
  const { user, loading } = useSession();
  if (loading) return <Loading />;
  if (user) return <Navigate to="/espaces" replace />;
  return (
    <PublicShell wide>
      <section className="landing">
        <p className="eyebrow">Gestion locative pour bailleurs particuliers</p>
        <h1 className="display">
          Louer en <mark className="bv-surligne">connaissance</mark> de cause.
        </h1>
        <p className="intro">Bailivo vérifie chaque dossier, vous montre d’où vient chaque information et vous accompagne jusqu’au dernier loyer.</p>
        <p className="row">
          <Link to="/inscription" className="bv-btn bv-btn-primaire">
            Commencer une location
          </Link>
          <Link to="/connexion" className="bv-btn bv-btn-discret">
            Se connecter
          </Link>
        </p>
        <p className="small muted">Candidat ou locataire ? Connectez-vous pour retrouver vos dossiers et votre location.</p>
      </section>
    </PublicShell>
  );
}

function NotFound() {
  return (
    <PublicShell>
      <h1>Page introuvable</h1>
      <p>Cette adresse ne correspond à aucune page.</p>
      <Link to="/">Revenir à l’accueil</Link>
    </PublicShell>
  );
}

const orgChildren: RouteObject[] = [
  { index: true, element: <OrgHomePage /> },
  { path: "parametres/membres", element: <MembersPage /> },
  { path: "parametres/envois", element: <OutboxPage /> },
  { path: "parametres/services", element: <ServicesPage /> },
  { path: "parametres/journal", element: <AuditPage /> },
  ...modules.flatMap((m) => m.orgRoutes ?? []),
];

const routes: RouteObject[] = [
  { path: "/", element: <Home /> },
  { path: "/connexion", element: <LoginPage /> },
  { path: "/inscription", element: <SignupPage /> },
  { path: "/mot-de-passe-oublie", element: <ForgotPage /> },
  { path: "/reinitialiser", element: <ResetPage /> },
  { path: "/invitation/:token", element: <InvitationPage /> },
  {
    path: "/espaces",
    element: (
      <RequireAuth>
        <OrgPickerPage />
      </RequireAuth>
    ),
  },
  {
    path: "/moi",
    element: (
      <RequireAuth>
        <AccountPage />
      </RequireAuth>
    ),
  },
  ...modules.flatMap((m) =>
    (m.accountRoutes ?? []).map((r) => ({ ...r, element: <RequireAuth>{r.element}</RequireAuth> })),
  ),
  ...modules.flatMap((m) => m.publicRoutes ?? []),
  {
    path: "/espace/:orgId",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: orgChildren,
  },
  { path: "*", element: <NotFound /> },
];

const router = createBrowserRouter(routes);

export default function App() {
  return <RouterProvider router={router} />;
}
